import { useEffect, useRef, useState, type RefObject } from 'react';

/**
 * ¿Se cumple una media query? Misma mecánica que KVGrid, con el punto de corte
 * leído de un token (`--evo-rail-bp`) para que el layout y el CSS no tengan
 * dos números distintos para la misma decisión.
 */
export function useAnchoMenorQue(tokenDeCorte: string): boolean {
  const consulta = (): string => {
    if (typeof window === 'undefined') return '(max-width: 0px)';
    const v = getComputedStyle(document.documentElement).getPropertyValue(tokenDeCorte).trim();
    return `(max-width: ${v || '0px'})`;
  };
  const [estrecho, setEstrecho] = useState(() =>
    typeof window !== 'undefined' ? window.matchMedia(consulta()).matches : false,
  );
  useEffect(() => {
    const mq = window.matchMedia(consulta());
    const handler = (e: MediaQueryListEvent) => { setEstrecho(e.matches); };
    setEstrecho(mq.matches);
    mq.addEventListener('change', handler);
    return () => { mq.removeEventListener('change', handler); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tokenDeCorte]);
  return estrecho;
}

/** El valor en px de un token de corte (`--evo-rail-bp: 1000px` → 1000). */
export function pxDeToken(token: string): number {
  if (typeof window === 'undefined') return 0;
  const v = getComputedStyle(document.documentElement).getPropertyValue(token).trim();
  const n = parseFloat(v);
  return Number.isNaN(n) ? 0 : n;
}

/**
 * El ancho REAL de un contenedor, medido con ResizeObserver.
 *
 * Una media query mira la ventana, y la ventana no es lo que hay: con el panel
 * de Evo Brain abierto, a 1440 px la columna de trabajo queda en ~460 px y un
 * layout que decidió por la ventana se aplasta (medido en producción,
 * 2026-09-06). Lo que decide es el ancho del contenedor.
 */
export function useAnchoDeContenedor<T extends HTMLElement>(): { ref: RefObject<T>; ancho: number | null } {
  const ref = useRef<T>(null);
  const [ancho, setAncho] = useState<number | null>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(entries => {
      const w = entries[0]?.contentRect.width;
      if (typeof w === 'number') setAncho(w);
    });
    ro.observe(el);
    setAncho(el.getBoundingClientRect().width);
    return () => { ro.disconnect(); };
  }, []);
  return { ref, ancho };
}
